import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import StatsCard from '../components/dashboard/StatsCard';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
    const { isDark } = useTheme();
    const [stats, setStats] = useState(null);
    const [charts, setCharts] = useState(null);
    const [recentSales, setRecentSales] = useState([]);
    const [loading, setLoading] = useState(true);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const response = await fetch('/api/v1/dashboard/summary');
            const data = await response.json();

            setStats(data.stats);
            setCharts(data.charts);
            setRecentSales(data.recent_sales);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return `GHC ${amount?.toLocaleString() || 0}`;
    };

    const styles = {
        container: {
            padding: '1rem 0',
            background: isDark ? '#0a0a0a' : '#f8f9fa',
            minHeight: '100vh',
            maxWidth: '100%',
            width: '100%',
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: '1rem',
            marginBottom: '2rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
        },
        chartsGrid: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '1.5rem',
            marginBottom: '2rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
        },
        chartCard: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: isMobile ? '1rem' : '1.5rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
        chartTitle: {
            fontSize: isMobile ? '1.1rem' : '1.25rem',
            fontWeight: 600,
            color: isDark ? '#ffffff' : '#1f2937',
            marginBottom: '1.5rem',
        },
        tableContainer: {
            background: isMobile ? 'transparent' : (isDark ? '#1e1e1e' : '#ffffff'),
            borderRadius: '1rem',
            padding: isMobile ? '0' : '1.5rem',
            boxShadow: isMobile ? 'none' : (isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'),
            marginLeft: isMobile ? '0' : '1rem',
            marginRight: isMobile ? '0' : '1rem',
        },
        tableTitle: {
            fontSize: '1.25rem',
            fontWeight: 600,
            color: isDark ? '#ffffff' : '#1f2937',
            marginBottom: '1rem',
            paddingLeft: isMobile ? '1rem' : '0',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            textAlign: 'left',
            padding: '0.75rem',
            borderBottom: `2px solid ${isDark ? '#333' : '#e5e7eb'}`,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: isDark ? '#9ca3af' : '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        },
        td: {
            padding: '0.75rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            color: isDark ? '#d1d5db' : '#374151',
        },
        badge: {
            padding: '0.25rem 0.75rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            display: 'inline-block',
        },
        badgeSuccess: {
            background: isDark ? '#065f46' : '#d1fae5',
            color: isDark ? '#34d399' : '#065f46',
        },
        badgeDanger: {
            background: isDark ? '#7f1d1d' : '#fee2e2',
            color: isDark ? '#fca5a5' : '#991b1b',
        },
        loading: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            fontSize: '1.5rem',
            color: isDark ? '#9ca3af' : '#6b7280',
        },
        // Mobile Card Styles
        card: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '1.25rem',
            marginBottom: '1rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            margin: '0 1rem 1rem 1rem',
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '1rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            paddingBottom: '0.75rem',
        },
        cardRow: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
        },
        cardLabel: {
            color: isDark ? '#9ca3af' : '#6b7280',
            fontWeight: 500,
        },
        cardValue: {
            color: isDark ? '#d1d5db' : '#374151',
            fontWeight: 600,
            textAlign: 'right',
        },
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>
                    <div>Loading dashboard...</div>
                </div>
            </div>
        );
    }

    // Prepare chart data
    const leaderboardData = charts?.uber_leaderboard
        ? Object.entries(charts.uber_leaderboard)
            .map(([name, amount]) => ({ name, amount }))
            .sort((a, b) => b.amount - a.amount)
            .slice(0, 10)
        : [];

    const allDriversSalesData = charts?.driver_sales
        ? Object.entries(charts.driver_sales)
            .map(([name, amount]) => ({ name, amount }))
        : [];

    const monthlySalesData = charts?.this_month_sales
        ? Object.entries(charts.this_month_sales)
            .map(([date, amount]) => ({ date: new Date(date).getDate(), amount }))
            .sort((a, b) => a.date - b.date)
        : [];

    const salesOverviewData = charts?.sales_overview
        ? Object.entries(charts.sales_overview)
            .map(([date, amount]) => ({ date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), amount }))
            .slice(-60) // Last 60 entries
        : [];

    const renderMobileSaleCard = (sale) => (
        <div key={sale.id} style={styles.card}>
            <div style={styles.cardHeader}>
                <div>
                    <div style={{ fontWeight: 'bold', fontSize: '1rem', color: isDark ? '#fff' : '#1f2937' }}>
                        {sale.driver}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                        {sale.car}
                    </div>
                </div>
                <span
                    style={{
                        ...styles.badge,
                        ...(sale.status === 'RECEIVED'
                            ? styles.badgeSuccess
                            : styles.badgeDanger),
                    }}
                >
                    {sale.status}
                </span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Date</span>
                <span style={styles.cardValue}>{sale.date}</span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Amount</span>
                <span style={{ ...styles.cardValue, color: '#10b981', fontSize: '1.1rem' }}>
                    {formatCurrency(sale.amount)}
                </span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Payment</span>
                <span style={styles.cardValue}>{sale.payment_mode}</span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Days Covered</span>
                <span style={styles.cardValue}>{sale.days}</span>
            </div>
        </div>
    );

    return (
        <div style={styles.container}>
            {/* Stats Cards */}
            <div style={styles.grid} className="dashboard-grid">
                <StatsCard
                    title="Total Cars"
                    value={stats?.cars || 0}
                    icon="🚗"
                    color="purple"
                />
                <StatsCard
                    title="Total Owners"
                    value={stats?.owners || 0}
                    icon="👥"
                    color="cyan"
                />
                <StatsCard
                    title="Total Drivers"
                    value={stats?.drivers || 0}
                    icon="🧑‍✈️"
                    color="green"
                />
                <StatsCard
                    title="Total Sales"
                    value={formatCurrency(stats?.sales_amount)}
                    icon="💰"
                    color="orange"
                />
            </div>

            {/* Charts Grid */}
            <div style={styles.chartsGrid}>
                {/* Driver Leaderboard */}
                <div style={styles.chartCard}>
                    <h3 style={styles.chartTitle}>Taxi/Uber Drivers Leader Board</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={leaderboardData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                            <XAxis type="number" stroke={isDark ? '#9ca3af' : '#6b7280'} />
                            <YAxis dataKey="name" type="category" width={80} stroke={isDark ? '#9ca3af' : '#6b7280'} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
                                    border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
                                    borderRadius: '0.5rem'
                                }}
                            />
                            <Bar dataKey="amount" fill="#3b82f6" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* All Drivers Sales */}
                <div style={styles.chartCard}>
                    <h3 style={styles.chartTitle}>All Drivers Sales Amount Graph</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={allDriversSalesData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                            <XAxis dataKey="name" stroke={isDark ? '#9ca3af' : '#6b7280'} angle={-45} textAnchor="end" height={80} />
                            <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
                                    border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
                                    borderRadius: '0.5rem'
                                }}
                            />
                            <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* This Month's Sales */}
                <div style={styles.chartCard}>
                    <h3 style={styles.chartTitle}>This Months Sales Graph</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={monthlySalesData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                            <XAxis dataKey="date" stroke={isDark ? '#9ca3af' : '#6b7280'} />
                            <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
                                    border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
                                    borderRadius: '0.5rem'
                                }}
                            />
                            <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Sales Submission Overview */}
                <div style={styles.chartCard}>
                    <h3 style={styles.chartTitle}>Sales Submission Overview</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={salesOverviewData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                            <XAxis dataKey="date" stroke={isDark ? '#9ca3af' : '#6b7280'} angle={-45} textAnchor="end" height={80} />
                            <YAxis stroke={isDark ? '#9ca3af' : '#6b7280'} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
                                    border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
                                    borderRadius: '0.5rem'
                                }}
                            />
                            <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Recent Sales Section */}
            <div style={styles.tableContainer}>
                <h3 style={styles.tableTitle}>Recent Sales</h3>
                {isMobile ? (
                    <div>
                        {recentSales.map(renderMobileSaleCard)}
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Payment</th>
                                    <th style={styles.th}>Days</th>
                                    <th style={styles.th}>Amount</th>
                                    <th style={styles.th}>Car</th>
                                    <th style={styles.th}>Driver</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentSales.map((sale) => (
                                    <tr key={sale.id}>
                                        <td style={styles.td}>{sale.date}</td>
                                        <td style={styles.td}>
                                            <span
                                                style={{
                                                    ...styles.badge,
                                                    ...(sale.status === 'RECEIVED'
                                                        ? styles.badgeSuccess
                                                        : styles.badgeDanger),
                                                }}
                                            >
                                                {sale.status}
                                            </span>
                                        </td>
                                        <td style={styles.td}>{sale.payment_mode}</td>
                                        <td style={styles.td}>{sale.days}</td>
                                        <td style={styles.td}>{formatCurrency(sale.amount)}</td>
                                        <td style={styles.td}>{sale.car}</td>
                                        <td style={styles.td}>{sale.driver}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
